import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Select,
  TimePicker,
  DatePicker,
  Radio,
  Row,
  Col,
  Card,
  List,
  Popover,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  FilterOutlined,
  ShareAltOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import './agendaCalendario.css';

// Función para obtener el lunes de la semana de una fecha
// dayjs usa domingo=0, lunes=1, ..., sábado=6
// Necesitamos calcular el lunes de la semana actual
const ajustarASemanaLunes = (fecha) => {
  const dia = fecha.day(); // 0=Domingo, 1=Lunes, ..., 6=Sábado
  // Si es domingo (0), retroceder 6 días para llegar al lunes anterior
  // Si es lunes (1), no retroceder (0 días)
  // Si es martes (2), retroceder 1 día
  // etc.
  const diasHastaLunes = dia === 0 ? -6 : 1 - dia;
  return fecha.add(diasHastaLunes, 'day').startOf('day');
};

const AgendaCalendario = () => {
  const [turnos, setTurnos] = useState([]);
  const [turnosMobile, setTurnosMobile] = useState([]);
  const [canchas, setCanchas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingTurnos, setLoadingTurnos] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTurno, setEditingTurno] = useState(null);
  const [semanaActual, setSemanaActual] = useState(ajustarASemanaLunes(dayjs()));
  const [diaSeleccionado, setDiaSeleccionado] = useState(dayjs().day() === 0 ? 7 : dayjs().day()); // 1-7 (Lunes-Domingo)
  const [alumnosFijos, setAlumnosFijos] = useState({}); // { alumnoId: true/false }
  const [alumnosAsisten, setAlumnosAsisten] = useState({}); // { alumnoId: true/false } - true = asiste, false = no asiste
  const [alumnosFijosEliminados, setAlumnosFijosEliminados] = useState({}); // { alumnoId: true }
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);
  const [tableWidth, setTableWidth] = useState(0);
  const tableWrapperRef = useRef(null);
  const [fechaSeleccionadaMobile, setFechaSeleccionadaMobile] = useState(dayjs());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [modalFiltroVisible, setModalFiltroVisible] = useState(false);

  const obtenerInicioSemana = () => ajustarASemanaLunes(semanaActual);

  // Días de la semana
  const diasSemana = [
    { key: 1, nombre: 'Lunes', abrev: 'LUN' },
    { key: 2, nombre: 'Martes', abrev: 'MAR' },
    { key: 3, nombre: 'Miércoles', abrev: 'MIE' },
    { key: 4, nombre: 'Jueves', abrev: 'JUE' },
    { key: 5, nombre: 'Viernes', abrev: 'VIE' },
    { key: 6, nombre: 'Sábado', abrev: 'SAB' },
    { key: 7, nombre: 'Domingo', abrev: 'DOM' },
  ];
  
  // Función auxiliar para obtener nombre del día
  const getDiaSemanaNombre = (diaNum) => {
    return diasSemana.find(d => d.key === diaNum)?.nombre || 'Desconocido';
  };

  // Generar horarios (cada 30 minutos de 8:00 a 22:00)
  const generarHorarios = () => {
    const horarios = [];
    for (let hora = 8; hora < 22; hora++) {
      horarios.push(`${hora.toString().padStart(2, '0')}:00`);
      horarios.push(`${hora.toString().padStart(2, '0')}:30`);
    }
    return horarios;
  };

  const horarios = generarHorarios();

  // Cargar datos iniciales
  const loadData = async () => {
    setLoadingInitial(true);
    try {
      // Cargar canchas, categorías, profesores y alumnos
      const [canchasRes, categoriasRes, profesoresRes, alumnosRes] = await Promise.all([
        apiClient.get('/canchas'),
        apiClient.get('/categorias'),
        apiClient.get('/profesores'),
        apiClient.get('/alumnos'),
      ]);

      if (canchasRes.ok) {
        const canchasData = await canchasRes.json();
        const canchasActivas = canchasData.filter(c => c.can_activo);
        setCanchas(canchasActivas);
        console.log('✅ Canchas cargadas:', canchasActivas.length, canchasActivas);
      } else {
        const errorText = await canchasRes.text();
        console.error('❌ Error al cargar canchas:', canchasRes.status, errorText);
        message.error('Error al cargar canchas');
      }

      if (categoriasRes.ok) {
        const categoriasData = await categoriasRes.json();
        const categoriasActivas = categoriasData.filter(c => c.cat_activo);
        setCategorias(categoriasActivas);
        console.log('✅ Categorías cargadas:', categoriasActivas.length, categoriasActivas);
      } else {
        const errorText = await categoriasRes.text();
        console.error('❌ Error al cargar categorías:', categoriasRes.status, errorText);
        message.error('Error al cargar categorías');
      }

      if (profesoresRes.ok) {
        const profesoresData = await profesoresRes.json();
        const profesoresActivos = profesoresData.filter(p => p.pro_activo);
        setProfesores(profesoresActivos);
        console.log('✅ Profesores cargados:', profesoresActivos.length, profesoresActivos);
      } else {
        const errorText = await profesoresRes.text();
        console.error('❌ Error al cargar profesores:', profesoresRes.status, errorText);
        message.error('Error al cargar profesores');
      }

      if (alumnosRes.ok) {
        const alumnosData = await alumnosRes.json();
        // El backend ahora devuelve { data, total, page, limit } con paginación
        // Si viene con estructura paginada, usar data; si es array directo, usar el array
        const alumnosArray = Array.isArray(alumnosData) ? alumnosData : (alumnosData.data || []);
        const alumnosActivos = alumnosArray.filter(a => a.alu_activo);
        setAlumnos(alumnosActivos);
        console.log('✅ Alumnos cargados:', alumnosActivos.length, alumnosActivos);
      } else {
        const errorText = await alumnosRes.text();
        console.error('❌ Error al cargar alumnos:', alumnosRes.status, errorText);
        message.error('Error al cargar alumnos');
      }

      // Cargar turnos para la semana actual
      await loadTurnos();
    } catch (error) {
      console.error('❌ Error general:', error);
      message.error('Error al cargar los datos: ' + error.message);
    } finally {
      setLoadingInitial(false);
    }
  };

  // Cargar turnos para la semana actual
  const loadTurnos = async () => {
    setLoadingTurnos(true);
    try {
      const inicioSemana = obtenerInicioSemana();
      const fechaDesde = inicioSemana.format('YYYY-MM-DD');
      const fechaHasta = inicioSemana.add(6, 'day').format('YYYY-MM-DD');
      const fechaConsulta = getFechaDiaSeleccionado().format('YYYY-MM-DD');
      
      // El backend ahora devuelve todos los datos optimizados (asistencia y profesor por fecha) en una sola llamada
      const response = await apiClient.get(`/turnos/rango?fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}&fechaConsulta=${fechaConsulta}`);
      if (response.ok) {
        const data = await response.json();
        // Los datos ya vienen con asistencia y profesor por fecha incluidos desde el backend
        setTurnos(data);
      } else {
        const errorText = await response.text();
        console.error('Error al cargar turnos:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Error al cargar turnos:', error);
    } finally {
      setLoadingTurnos(false);
    }
  };

  // Obtener la fecha del día seleccionado
  const getFechaDiaSeleccionado = () => {
    // Si estamos en mobile, usar la fecha seleccionada directamente
    if (isMobile && fechaSeleccionadaMobile) {
      return fechaSeleccionadaMobile;
    }
    // En desktop, calcular desde la semana actual
    const inicioSemana = obtenerInicioSemana();
    return inicioSemana.add(diaSeleccionado - 1, 'day');
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recargar asistencia cuando cambia la fecha seleccionada y el modal está abierto
  useEffect(() => {
    if (modalVisible && editingTurno && editingTurno.tur_tipo === 'FIJO') {
      const recargarAsistencia = async () => {
        try {
          const fechaDia = getFechaDiaSeleccionado().format('YYYY-MM-DD');
          const asistenciaResponse = await apiClient.get(`/turnos/asistencia?turnoId=${editingTurno.tur_id}&fecha=${fechaDia}`);
          if (asistenciaResponse.ok) {
            const asistenciaMap = await asistenciaResponse.json();
            // Actualizar el estado de asistencia de los alumnos
            const nuevosAsisten = { ...alumnosAsisten };
            if (editingTurno.alumnos) {
              editingTurno.alumnos.forEach(alumno => {
                const esFijo = alumno.tua_fijo === true || alumno.tua_fijo === 'true' || alumno.tua_fijo === 1 || alumno.tua_fijo === '1';
                if (esFijo && asistenciaMap[alumno.alu_id] !== undefined) {
                  nuevosAsisten[alumno.alu_id] = asistenciaMap[alumno.alu_id];
                } else if (esFijo && asistenciaMap[alumno.alu_id] === undefined) {
                  // Si no hay registro para esta fecha, usar true por defecto
                  nuevosAsisten[alumno.alu_id] = true;
                }
              });
            }
            setAlumnosAsisten(nuevosAsisten);
          }
        } catch (error) {
          console.error('Error al recargar asistencia:', error);
        }
      };
      recargarAsistencia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diaSeleccionado, semanaActual, fechaSeleccionadaMobile, modalVisible]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (tableWrapperRef.current) {
        setTableWidth(tableWrapperRef.current.getBoundingClientRect().width);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!tableWrapperRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach(entry => {
        setTableWidth(entry.contentRect.width);
      });
    });

    observer.observe(tableWrapperRef.current);
    setTableWidth(tableWrapperRef.current.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadTurnos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semanaActual, diaSeleccionado, fechaSeleccionadaMobile]);

  // Cargar turnos móviles cuando cambie la fecha seleccionada
  useEffect(() => {
    if (isMobile && fechaSeleccionadaMobile) {
      const cargarTurnosMobile = async () => {
        try {
          const turnos = await getTurnosPorFecha(fechaSeleccionadaMobile);
          setTurnosMobile(turnos);
        } catch (error) {
          console.error('Error al cargar turnos móviles:', error);
          setTurnosMobile([]);
        }
      };
      cargarTurnosMobile();
    } else {
      setTurnosMobile([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, fechaSeleccionadaMobile, turnos, filtroCategoria]);

  // Cargar turnos cuando cambia la fecha en mobile
  useEffect(() => {
    if (isMobile && fechaSeleccionadaMobile) {
      // Ajustar semana actual para incluir la fecha seleccionada
      const nuevaSemana = ajustarASemanaLunes(fechaSeleccionadaMobile);
      setSemanaActual(nuevaSemana);
      // Calcular el día de la semana (1-7, donde 1 es lunes)
      const diaSemana = fechaSeleccionadaMobile.day() === 0 ? 7 : fechaSeleccionadaMobile.day();
      setDiaSeleccionado(diaSemana);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaSeleccionadaMobile, isMobile]);

  // Obtener turnos para el día seleccionado y cancha específica
  const getTurnosPorDiaYCancha = (canchaId) => {
    const fechaDia = getFechaDiaSeleccionado();
    const turnosFiltrados = turnos.filter(turno => {
      if (turno.tur_tipo === 'FIJO') {
        // Turno fijo: verificar si el día de la semana coincide
        // Validar que tenga tur_dia_semana configurado
        if (!turno.tur_dia_semana) {
          return false;
        }
        
        // Validar que tenga cancha asignada
        if (!turno.tur_cancha) {
          return false;
        }
        
        // Validar que esté activo
        if (!turno.tur_activo) {
          return false;
        }
        
        // Convertir ambos a número para comparación estricta
        const diaSemanaTurno = Number(turno.tur_dia_semana);
        const diaSeleccionadoNum = Number(diaSeleccionado);
        const canchaIdNum = Number(canchaId);
        const turnoCanchaNum = Number(turno.tur_cancha);
        
        const coincideDia = diaSemanaTurno === diaSeleccionadoNum && turnoCanchaNum === canchaIdNum;
        
        // Aplicar filtro de categoría si está activo
        if (filtroCategoria && coincideDia) {
          return Number(turno.tur_categoria) === Number(filtroCategoria);
        }
        return coincideDia;
      } else {
        // EVENTO: verificar si la fecha específica coincide con el día seleccionado
        const fechaTurno = dayjs(turno.tur_fecha_especifica);
        const coincideFecha = fechaTurno.format('YYYY-MM-DD') === fechaDia.format('YYYY-MM-DD') 
          && Number(turno.tur_cancha) === Number(canchaId)
          && turno.tur_activo;
        // Aplicar filtro de categoría si está activo
        if (filtroCategoria && coincideFecha) {
          return Number(turno.tur_categoria) === Number(filtroCategoria);
        }
        return coincideFecha;
      }
    });
    
    return turnosFiltrados;
  };

  // Obtener todos los turnos del día seleccionado (sin filtrar por cancha)
  const getTurnosDelDia = () => {
    const fechaDia = getFechaDiaSeleccionado();
    const turnosFiltrados = turnos.filter(turno => {
      if (!turno.tur_activo) return false;
      
      if (turno.tur_tipo === 'FIJO') {
        // Turno fijo: verificar si el día de la semana coincide
        if (!turno.tur_dia_semana) return false;
        const diaSemanaTurno = Number(turno.tur_dia_semana);
        const diaSeleccionadoNum = Number(diaSeleccionado);
        const coincideDia = diaSemanaTurno === diaSeleccionadoNum;
        
        // Aplicar filtro de categoría si está activo
        if (filtroCategoria && coincideDia) {
          return Number(turno.tur_categoria) === Number(filtroCategoria);
        }
        return coincideDia;
      } else {
        // EVENTO: verificar si la fecha específica coincide con el día seleccionado
        const fechaTurno = dayjs(turno.tur_fecha_especifica);
        const coincideFecha = fechaTurno.format('YYYY-MM-DD') === fechaDia.format('YYYY-MM-DD');
        
        // Aplicar filtro de categoría si está activo
        if (filtroCategoria && coincideFecha) {
          return Number(turno.tur_categoria) === Number(filtroCategoria);
        }
        return coincideFecha;
      }
    });
    
    return turnosFiltrados;
  };

  // Función para generar mensaje de aviso a profesores
  const handleAvisoProfesores = () => {
    try {
      const fechaDia = getFechaDiaSeleccionado();
      const fechaTexto = fechaDia.format('DD/MM/YYYY');
      const nombreDia = getDiaSemanaNombre(diaSeleccionado);
      
      // Obtener todos los turnos del día
      const turnosDelDia = getTurnosDelDia();
      
      if (turnosDelDia.length === 0) {
        message.warning('No hay turnos programados para este día');
        return;
      }
      
      // Agrupar turnos por profesor
      const turnosPorProfesor = {};
      
      turnosDelDia.forEach(turno => {
        const profesorId = turno.tur_profesor;
        const profesorNombre = turno.profesor_nombre || 'Sin asignar';
        const profesorApellido = turno.profesor_apellido || '';
        const nombreCompleto = `${profesorNombre} ${profesorApellido}`.trim();
        
        // Obtener información de cancha y categoría
        const cancha = canchas.find(c => c.can_id === turno.tur_cancha);
        const categoria = categorias.find(c => c.cat_id === turno.tur_categoria);
        
        if (!turnosPorProfesor[nombreCompleto]) {
          turnosPorProfesor[nombreCompleto] = [];
        }
        
        turnosPorProfesor[nombreCompleto].push({
          horario: `${turno.tur_hora_desde} - ${turno.tur_hora_hasta}`,
          cancha: cancha?.can_descripcion || 'Sin cancha',
          categoria: categoria?.cat_descripcion || 'Sin categoría',
        });
      });
      
      // Construir mensaje
      let mensaje = `*Aviso de Turnos - ${nombreDia} ${fechaTexto}*\n\n`;
      
      // Ordenar profesores alfabéticamente
      const profesoresOrdenados = Object.keys(turnosPorProfesor).sort();
      
      profesoresOrdenados.forEach((nombreProfesor, index) => {
        const turnosProfesor = turnosPorProfesor[nombreProfesor];
        
        mensaje += `*${nombreProfesor}:*\n`;
        
        turnosProfesor.forEach((turno, idx) => {
          mensaje += `• ${turno.horario} - ${turno.cancha} (${turno.categoria})\n`;
        });
        
        // Agregar línea separadora si no es el último profesor
        if (index < profesoresOrdenados.length - 1) {
          mensaje += `\n`;
        }
      });
      
      // Codificar el mensaje para URL
      const mensajeCodificado = encodeURIComponent(mensaje);
      
      // Generar enlace de WhatsApp
      const whatsappUrl = `https://wa.me/?text=${mensajeCodificado}`;
      
      // Abrir en nueva pestaña
      window.open(whatsappUrl, '_blank');
      
      message.success('Mensaje de aviso generado. Puedes compartirlo en el grupo de WhatsApp de profesores.');
    } catch (error) {
      console.error('Error al generar aviso a profesores:', error);
      message.error('Error al generar el mensaje de aviso');
    }
  };

  // Obtener todos los turnos de una fecha específica (para mobile)
  const getTurnosPorFecha = async (fecha) => {
    const fechaFormato = fecha.format('YYYY-MM-DD');
    const diaSemana = fecha.day() === 0 ? 7 : fecha.day(); // Domingo = 7
    
    // Usar los turnos ya cargados en memoria (optimizado - sin llamadas HTTP adicionales)
    // Los turnos ya vienen con asistencia y profesor por fecha desde loadTurnos()
    const turnosFiltrados = turnos.filter(turno => {
      if (!turno.tur_activo) return false;
      
      let coincide = false;
      if (turno.tur_tipo === 'FIJO') {
        // Turno fijo: verificar si el día de la semana coincide
        coincide = turno.tur_dia_semana === diaSemana;
      } else {
        // EVENTO: verificar si la fecha específica coincide
        const fechaTurno = dayjs(turno.tur_fecha_especifica);
        coincide = fechaTurno.format('YYYY-MM-DD') === fechaFormato;
      }
      
      // Aplicar filtro de categoría si está activo
      if (filtroCategoria && coincide) {
        return turno.tur_categoria === filtroCategoria;
      }
      
      return coincide;
    });
    
    // Los datos ya vienen con profesor por fecha incluido desde el backend
    // Solo necesitamos filtrar alumnos según la fecha
    const turnosConAlumnosFiltrados = turnosFiltrados.map(turno => {
      const alumnosFiltrados = filtrarAlumnosPorFechaParaMobile(turno, fecha);
      return { ...turno, alumnosFiltrados };
    });
    
    return turnosConAlumnosFiltrados.sort((a, b) => {
      // Ordenar por horario de inicio
      const [horaA, minA] = a.tur_hora_desde.split(':').map(Number);
      const [horaB, minB] = b.tur_hora_desde.split(':').map(Number);
      const minutosA = horaA * 60 + minA;
      const minutosB = horaB * 60 + minB;
      return minutosA - minutosB;
    });
  };

  // Filtrar alumnos por fecha para mobile
  const filtrarAlumnosPorFechaParaMobile = (turno, fecha) => {
    if (!turno.alumnos || turno.alumnos.length === 0) {
      return [];
    }

    const fechaDia = fecha.format('YYYY-MM-DD');
    
    if (turno.tur_tipo === 'FIJO') {
      return turno.alumnos.filter(alumno => {
        const esFijo = alumno.tua_fijo === true || 
                       alumno.tua_fijo === 'true' || 
                       alumno.tua_fijo === 1 || 
                       alumno.tua_fijo === '1';
        
        if (esFijo) {
          return true;
        }
        
        if (!alumno.tua_fecha_especifica) {
          return false;
        }
        
        const fechaAlumno = dayjs(alumno.tua_fecha_especifica).format('YYYY-MM-DD');
        return fechaAlumno === fechaDia;
      });
    }
    
    return turno.alumnos;
  };

  // Verificar si un horario está dentro de un turno
  const estaEnTurno = (horario, turno) => {
    const [hora, minuto] = horario.split(':').map(Number);
    const [desdeH, desdeM] = turno.tur_hora_desde.split(':').map(Number);
    const [hastaH, hastaM] = turno.tur_hora_hasta.split(':').map(Number);
    
    const horarioMinutos = hora * 60 + minuto;
    const desdeMinutos = desdeH * 60 + desdeM;
    const hastaMinutos = hastaH * 60 + hastaM;
    
    return horarioMinutos >= desdeMinutos && horarioMinutos < hastaMinutos;
  };

  // Verificar si un horario es el inicio de un turno
  const esInicioTurno = (horario, turno) => {
    if (!turno || !turno.tur_hora_desde) {
      return false;
    }
    
    const [hora, minuto] = horario.split(':').map(Number);
    // tur_hora_desde puede venir como "HH:MM:SS" o "HH:MM"
    const horaDesdeParts = turno.tur_hora_desde.split(':');
    const desdeH = Number(horaDesdeParts[0]);
    const desdeM = Number(horaDesdeParts[1]);
    
    // Convertir ambos horarios a minutos para comparar
    const horarioMinutos = hora * 60 + minuto;
    const desdeMinutos = desdeH * 60 + desdeM;
    
    // El turno debe comenzar en este slot exacto o dentro de los primeros 30 minutos de este slot
    // Ejemplo: turno a las 19:10 debe aparecer en el slot de 19:00 (no en 19:30)
    // Diferencia positiva significa que el turno comienza después del inicio del slot
    const diferencia = desdeMinutos - horarioMinutos;
    
    // El turno comienza en este slot (coincidencia exacta) o dentro de los primeros 30 minutos de este slot
    // No debe comenzar antes del slot (diferencia negativa)
    const coincide = diferencia >= 0 && diferencia < 30;
    
    return coincide;
  };

  // Calcular cuántos slots ocupa un turno
  const calcularSlotsTurno = (turno) => {
    const [desdeH, desdeM] = turno.tur_hora_desde.split(':').map(Number);
    const [hastaH, hastaM] = turno.tur_hora_hasta.split(':').map(Number);
    
    const desdeMinutos = desdeH * 60 + desdeM;
    const hastaMinutos = hastaH * 60 + hastaM;
    const duracionMinutos = hastaMinutos - desdeMinutos;
    
    // Cada slot es de 30 minutos
    return Math.ceil(duracionMinutos / 30);
  };

  // Abrir modal para crear nuevo turno
  const handleCreate = () => {
    setEditingTurno(null);
    setAlumnosFijos({});
    setAlumnosAsisten({});
    setAlumnosFijosEliminados({});
    form.resetFields();
    form.setFieldsValue({ 
      tipo: 'FIJO',
      activo: true,
      profesor: undefined, // No asignar profesor por defecto para turnos fijos
    });
    setModalVisible(true);
  };

  // Función para compartir disponibilidad del turno por WhatsApp
  const handleCompartirDisponibilidad = () => {
    try {
      const formValues = form.getFieldsValue();
      
      // Obtener datos del turno
      const tipo = formValues.tipo || editingTurno?.tur_tipo;
      const horaDesde = formValues.horaDesde ? formValues.horaDesde.format('HH:mm') : (editingTurno?.tur_hora_desde || '');
      const horaHasta = formValues.horaHasta ? formValues.horaHasta.format('HH:mm') : (editingTurno?.tur_hora_hasta || '');
      
      // Obtener información de cancha, categoría y profesor
      const canchaId = formValues.cancha || editingTurno?.tur_cancha;
      const categoriaId = formValues.categoria || editingTurno?.tur_categoria;
      const profesorId = formValues.profesor || editingTurno?.tur_profesor;
      
      const cancha = canchas.find(c => c.can_id === canchaId);
      const categoria = categorias.find(c => c.cat_id === categoriaId);
      const profesor = profesores.find(p => p.pro_id === profesorId);
      
      // Obtener fecha
      let fechaTexto = '';
      if (tipo === 'FIJO') {
        const fechaDia = getFechaDiaSeleccionado();
        const diaSemana = formValues.diaSemana || editingTurno?.tur_dia_semana;
        const nombreDia = diasSemana.find(d => d.key === diaSemana)?.nombre || '';
        fechaTexto = `${nombreDia} ${fechaDia.format('DD/MM/YYYY')}`;
      } else {
        const fechaEspecifica = formValues.fechaEspecifica || (editingTurno?.tur_fecha_especifica ? dayjs(editingTurno.tur_fecha_especifica) : null);
        if (fechaEspecifica) {
          fechaTexto = fechaEspecifica.format('DD/MM/YYYY');
        }
      }
      
      // Calcular lugares disponibles
      const cantidadMaxima = formValues.cantidadAlumnos || editingTurno?.tur_cantidad_alumnos || 0;
      
      // Obtener alumnos actuales: del form o del turno editado
      let alumnosActuales = 0;
      if (formValues.alumnos && formValues.alumnos.length > 0) {
        alumnosActuales = formValues.alumnos.length;
      } else if (editingTurno) {
        // Si estamos editando, contar alumnos del turno
        if (editingTurno.alumnos && Array.isArray(editingTurno.alumnos)) {
          alumnosActuales = editingTurno.alumnos.length;
        } else if (editingTurno.alumnosFiltrados && Array.isArray(editingTurno.alumnosFiltrados)) {
          alumnosActuales = editingTurno.alumnosFiltrados.length;
        }
      }
      
      const lugaresDisponibles = cantidadMaxima > 0 ? Math.max(0, cantidadMaxima - alumnosActuales) : null;
      
      // Construir mensaje
      let mensaje = `*Disponibilidad de Turno*\n\n`;
      mensaje += `*Fecha:* ${fechaTexto}\n`;
      mensaje += `*Horario:* ${horaDesde} - ${horaHasta}\n`;
      if (cancha) {
        mensaje += `*Cancha:* ${cancha.can_descripcion}\n`;
      }
      if (categoria) {
        mensaje += `*Categoría:* ${categoria.cat_descripcion}\n`;
      }
      if (profesor) {
        mensaje += `*Profesor:* ${profesor.pro_nombre} ${profesor.pro_apellido}\n`;
      }
      
      // Agregar información de disponibilidad
      if (lugaresDisponibles !== null) {
        if (lugaresDisponibles > 0) {
          mensaje += `\n*Lugares disponibles:* ${lugaresDisponibles} de ${cantidadMaxima}\n`;
        } else {
          mensaje += `\n*Turno completo* (${alumnosActuales}/${cantidadMaxima})\n`;
        }
      } else {
        mensaje += `\n👥 *Alumnos inscritos:* ${alumnosActuales}\n`;
      }
      
      mensaje += `\n ¿Te interesa? Contáctanos para más información.`;
      
      // Codificar el mensaje para URL
      const mensajeCodificado = encodeURIComponent(mensaje);
      
      // Generar enlace de WhatsApp
      const whatsappUrl = `https://wa.me/?text=${mensajeCodificado}`;
      
      // Abrir en nueva pestaña
      window.open(whatsappUrl, '_blank');
      
      message.success('Enlace de WhatsApp generado. Puedes compartirlo donde quieras.');
    } catch (error) {
      console.error('Error al generar enlace de WhatsApp:', error);
      message.error('Error al generar el enlace de compartir');
    }
  };

  // Abrir modal para editar turno
  const handleEdit = async (turno) => {
    setLoadingEdit(true);
    try {
    // Si es turno FIJO, cargar asistencia por fecha y profesor por fecha desde el backend
    if (turno.tur_tipo === 'FIJO') {
      const fechaDia = getFechaDiaSeleccionado().format('YYYY-MM-DD');
      try {
        const response = await apiClient.get(`/turnos/${turno.tur_id}?fecha=${fechaDia}`);
        if (response.ok) {
          const turnoConAsistencia = await response.json();
          turno = turnoConAsistencia; // Actualizar turno con asistencia por fecha y profesor por fecha
        }
        
        // También cargar profesor por fecha por si acaso
        const profesorResponse = await apiClient.get(`/turnos/profesor?turnoId=${turno.tur_id}&fecha=${fechaDia}`);
        if (profesorResponse.ok) {
          const profesorData = await profesorResponse.json();
          if (profesorData) {
            turno.tur_profesor = profesorData.profesor_id;
            turno.profesor_nombre = profesorData.profesor_nombre;
            turno.profesor_apellido = profesorData.profesor_apellido;
          }
        }
      } catch (error) {
        console.error('Error al cargar datos por fecha:', error);
      }
    }
      
      // Si el turno viene de mobile y tiene alumnosFiltrados, usar esos como alumnos base
      // pero necesitamos todos los alumnos para el merge, así que cargamos el turno completo
      if (isMobile && turno.alumnosFiltrados && !turno.alumnos) {
        // Si solo tenemos alumnosFiltrados, necesitamos cargar el turno completo
        try {
          const fechaDia = getFechaDiaSeleccionado().format('YYYY-MM-DD');
          const response = await apiClient.get(`/turnos/${turno.tur_id}${turno.tur_tipo === 'FIJO' ? `?fecha=${fechaDia}` : ''}`);
          if (response.ok) {
            const turnoCompleto = await response.json();
            turno = { ...turno, alumnos: turnoCompleto.alumnos || turno.alumnosFiltrados };
          }
        } catch (error) {
          console.error('Error al cargar turno completo:', error);
          // Si falla, usar alumnosFiltrados como fallback
          turno.alumnos = turno.alumnosFiltrados;
      }
    }
    
    // Guardar el turno completo con todos los alumnos (sin filtrar) para poder hacer merge al guardar
    setEditingTurno({
      ...turno,
      todosLosAlumnos: turno.alumnos || [] // Guardar todos los alumnos originales
    });
    
    // Filtrar alumnos según la fecha del día seleccionado (solo para turnos fijos) para mostrar en el modal
    let alumnosParaMostrar = turno.alumnos || [];
    if (turno.tur_tipo === 'FIJO') {
      alumnosParaMostrar = filtrarAlumnosPorFecha(turno);
    }
    
    // Inicializar el estado de alumnos fijos y asistencia desde los datos del turno filtrado
    const fijosMap = {};
    const asistenMap = {};
    if (alumnosParaMostrar && alumnosParaMostrar.length > 0) {
      alumnosParaMostrar.forEach(alumno => {
        fijosMap[alumno.alu_id] = alumno.tua_fijo !== undefined ? alumno.tua_fijo : (turno.tur_tipo === 'FIJO');
        // Para alumnos fijos, por defecto asisten (true)
        // Solo si hay un registro explícito que indique que no asiste, usar false
        if (alumno.tua_asiste !== undefined) {
          // Convertir a booleano explícitamente
          const valorAsistencia = alumno.tua_asiste;
          if (valorAsistencia === false || valorAsistencia === 'false' || valorAsistencia === 0 || valorAsistencia === '0') {
            asistenMap[alumno.alu_id] = false;
          } else {
            asistenMap[alumno.alu_id] = true;
          }
        } else {
          // Por defecto asiste (true) para alumnos fijos
          asistenMap[alumno.alu_id] = true;
        }
      });
    }
    setAlumnosFijos(fijosMap);
    setAlumnosAsisten(asistenMap);
    setAlumnosFijosEliminados({});
    
    form.setFieldsValue({
      tipo: turno.tur_tipo,
      diaSemana: turno.tur_dia_semana,
      fechaEspecifica: turno.tur_fecha_especifica ? dayjs(turno.tur_fecha_especifica) : null,
      horaDesde: turno.tur_hora_desde ? dayjs(turno.tur_hora_desde, 'HH:mm') : null,
      horaHasta: turno.tur_hora_hasta ? dayjs(turno.tur_hora_hasta, 'HH:mm') : null,
      cancha: turno.tur_cancha,
      categoria: turno.tur_categoria,
      profesor: turno.tur_profesor,
      cantidadAlumnos: turno.tur_cantidad_alumnos,
      // Mostrar solo los alumnos filtrados en el modal
      alumnos: alumnosParaMostrar.map(a => a.alu_id) || [],
      activo: turno.tur_activo,
    });
    setModalVisible(true);
    } catch (error) {
      console.error('Error al editar turno:', error);
      message.error('Error al cargar el turno para editar');
    } finally {
      setLoadingEdit(false);
    }
  };

  // Guardar turno (crear o actualizar)
  const handleSave = async () => {
    setLoadingSave(true);
    try {
      const values = await form.validateFields();
      
      // Obtener la fecha del día seleccionado en el calendario
      const fechaDiaSeleccionado = getFechaDiaSeleccionado().format('YYYY-MM-DD');
      
      console.log('💾 Guardando turno:', {
        tipo: values.tipo,
        fechaDiaSeleccionado,
        diaSeleccionado,
        semanaActual: semanaActual.format('YYYY-MM-DD')
      });
      
      // Preparar alumnos con información de si son fijos o no
      let alumnosData = [];
      
      // Calcular IDs de alumnos fijos eliminados una sola vez
      const alumnosFijosEliminadosIds = Object.keys(alumnosFijosEliminados)
        .filter(id => alumnosFijosEliminados[id])
        .map(id => Number(id));
      
      if (editingTurno && editingTurno.tur_tipo === 'FIJO' && editingTurno.todosLosAlumnos) {
        // Para turnos fijos editados, hacer merge: mantener alumnos de otras fechas y actualizar los de la fecha actual
        const fechaDia = fechaDiaSeleccionado;
        const alumnosEnModal = values.alumnos || [];
        
        // Alumnos que NO están en el modal pero están en el turno original
        // Estos son alumnos no fijos de otras fechas o alumnos fijos que se eliminaron del modal
        const alumnosDeOtrasFechas = editingTurno.todosLosAlumnos.filter(alumno => {
          // Excluir alumnos que están en el modal (se actualizarán desde el modal)
          if (alumnosEnModal.includes(alumno.alu_id)) {
            return false;
          }
          
          const esFijo = alumno.tua_fijo === true || alumno.tua_fijo === 'true' || alumno.tua_fijo === 1 || alumno.tua_fijo === '1';
          const marcadoParaEliminar = alumnosFijosEliminados[alumno.alu_id];
          
          // Si está marcado para eliminar, no mantenerlo
          if (marcadoParaEliminar) {
            return false;
          }
          
          // Mantener alumnos fijos que NO están en el modal y NO están marcados para eliminar
          if (esFijo) {
            return true; // Los alumnos fijos se mantienen si no están en el modal y no están marcados para eliminar
          }
          
          // Mantener alumnos no fijos de otras fechas
          if (alumno.tua_fecha_especifica) {
            const fechaAlumno = dayjs(alumno.tua_fecha_especifica).format('YYYY-MM-DD');
            return fechaAlumno !== fechaDia; // Mantener solo si es de otra fecha
          }
          
          // Eliminar alumnos no fijos sin fecha específica (casos raros)
          return false;
        });
        
        // Alumnos del modal (fecha actual)
        const alumnosFechaActual = alumnosEnModal
          .filter(id => !alumnosFijosEliminadosIds.includes(Number(id)))
          .map(id => {
            const esFijo = alumnosFijos[id] !== undefined ? alumnosFijos[id] : true;
            const fechaEspecifica = !esFijo ? fechaDia : null;
            const asiste = alumnosAsisten[id] !== undefined ? alumnosAsisten[id] : true;
            
            return {
              id,
              fijo: esFijo,
              fechaEspecifica,
              asiste
            };
          });
        
        // Combinar: alumnos de otras fechas + alumnos de la fecha actual
        // Excluir cualquier alumno que esté marcado para eliminar
        alumnosData = [
          ...alumnosDeOtrasFechas
            .filter(a => !alumnosFijosEliminadosIds.includes(Number(a.alu_id)))
            .map(a => ({
              id: a.alu_id,
              fijo: a.tua_fijo,
              fechaEspecifica: a.tua_fecha_especifica,
              asiste: a.tua_asiste !== undefined ? a.tua_asiste : true
            })),
          ...alumnosFechaActual
        ];
      } else {
        // Para turnos nuevos o eventos, usar la lógica normal
        alumnosData = (values.alumnos || []).map(id => {
          const esFijo = alumnosFijos[id] !== undefined ? alumnosFijos[id] : (values.tipo === 'FIJO');
          const fechaEspecifica = (values.tipo === 'FIJO' && !esFijo) ? fechaDiaSeleccionado : null;
          const asiste = alumnosAsisten[id] !== undefined ? alumnosAsisten[id] : true;
          
          return {
            id,
            fijo: esFijo,
            fechaEspecifica,
            asiste
          };
        });
      }
      
      const dataToSend = {
        tipo: values.tipo,
        diaSemana: values.tipo === 'FIJO' ? values.diaSemana : null,
        fechaEspecifica: values.tipo === 'EVENTO' ? values.fechaEspecifica.format('YYYY-MM-DD') : null,
        horaDesde: values.horaDesde.format('HH:mm'),
        horaHasta: values.horaHasta.format('HH:mm'),
        cancha: values.cancha,
        categoria: values.categoria || null,
        // Para turnos fijos nuevos, NO enviar profesor (se asignará individualmente por fecha)
        // Para turnos fijos editados o eventos, enviar el profesor
        profesor: (values.tipo === 'FIJO' && !editingTurno) ? null : (values.profesor || null),
        cantidadAlumnos: values.cantidadAlumnos || null,
        alumnos: alumnosData,
        activo: values.activo,
        // Agregar fecha del día seleccionado para guardar asistencia por fecha en turnos fijos
        fechaDiaSeleccionado: values.tipo === 'FIJO' ? fechaDiaSeleccionado : null,
        alumnosFijosEliminados: alumnosFijosEliminadosIds,
      };
      
      if (editingTurno) {
        const response = await apiClient.put(`/turnos/${editingTurno.tur_id}`, dataToSend);
        if (response.ok) {
          message.success('Turno actualizado correctamente');
          setModalVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el turno');
        }
      } else {
        const response = await apiClient.post('/turnos', dataToSend);
        if (response.ok) {
          message.success('Turno creado correctamente');
          setModalVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el turno');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) return;
      message.error('Error al guardar el turno');
    } finally {
      setLoadingSave(false);
    }
  };

  // Eliminar turno
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/turnos/${id}`);
      if (response.ok) {
        message.success('Turno eliminado correctamente');
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el turno');
    }
  };

  // Navegar semanas
  const semanaAnterior = () => {
    setSemanaActual(prev => ajustarASemanaLunes(prev.subtract(1, 'week')));
  };

  const semanaSiguiente = () => {
    setSemanaActual(prev => ajustarASemanaLunes(prev.add(1, 'week')));
  };

  const irAHoy = () => {
    setSemanaActual(ajustarASemanaLunes(dayjs()));
    const diaHoy = dayjs().day();
    setDiaSeleccionado(diaHoy === 0 ? 7 : diaHoy); // Domingo = 7
  };

  // Filtrar alumnos según la fecha del día seleccionado
  const filtrarAlumnosPorFecha = (turno) => {
    if (!turno.alumnos || turno.alumnos.length === 0) {
      return [];
    }

    const fechaDia = getFechaDiaSeleccionado().format('YYYY-MM-DD');
    
    // Si es turno FIJO, filtrar alumnos:
    // - Alumnos fijos (tua_fijo = true) aparecen siempre
    // - Alumnos no fijos (tua_fijo = false) solo aparecen si su fecha específica coincide
    if (turno.tur_tipo === 'FIJO') {
      const alumnosFiltrados = turno.alumnos.filter(alumno => {
        // Verificar si es fijo (puede venir como boolean, string "true"/"false", o número 1/0)
        const esFijo = alumno.tua_fijo === true || 
                       alumno.tua_fijo === 'true' || 
                       alumno.tua_fijo === 1 || 
                       alumno.tua_fijo === '1';
        
        // Si es fijo, siempre aparece
        if (esFijo) {
          console.log(`✅ Alumno ${alumno.alu_nombre} es FIJO - aparece siempre`);
          return true;
        }
        
        // Si no es fijo, solo aparece si la fecha específica coincide
        if (!alumno.tua_fecha_especifica) {
          console.log(`❌ Alumno ${alumno.alu_nombre} NO es fijo y NO tiene fecha específica - NO aparece`);
          return false; // Si no tiene fecha específica y no es fijo, no aparece
        }
        
        // Normalizar la fecha para comparar (puede venir como string con hora o solo fecha)
        const fechaAlumno = dayjs(alumno.tua_fecha_especifica).format('YYYY-MM-DD');
        const coincide = fechaAlumno === fechaDia;
        
        console.log(`🔍 Alumno ${alumno.alu_nombre}:`, {
          esFijo: false,
          fechaAlumno,
          fechaDia,
          coincide,
          resultado: coincide ? '✅ Aparece' : '❌ NO aparece'
        });
        
        return coincide;
      });
      
      console.log(`📊 Turno ${turno.tur_id} (${turno.tur_tipo}): ${turno.alumnos.length} alumnos totales, ${alumnosFiltrados.length} alumnos filtrados para fecha ${fechaDia}`);
      
      return alumnosFiltrados;
    }
    
    // Para eventos, todos los alumnos aparecen
    return turno.alumnos;
  };

  const actualizarSeleccionAlumnos = (nuevosIds) => {
    form.setFieldsValue({ alumnos: nuevosIds });
  };

  const quitarAlumnoDeSeleccion = (id, { eliminarFijo = false } = {}) => {
    const actuales = form.getFieldValue('alumnos') || [];
    const actualizados = actuales.filter(alumnoId => alumnoId !== id);
    actualizarSeleccionAlumnos(actualizados);

    setAlumnosFijos(prev => {
      const copia = { ...prev };
      delete copia[id];
      return copia;
    });

    setAlumnosAsisten(prev => {
      const copia = { ...prev };
      delete copia[id];
      return copia;
    });

    if (eliminarFijo) {
      setAlumnosFijosEliminados(prev => ({ ...prev, [id]: true }));
    }
  };

  const marcarAlumnoComoFijo = (id, esFijo) => {
    setAlumnosFijos(prev => ({
      ...prev,
      [id]: esFijo,
    }));

    if (esFijo) {
      setAlumnosFijosEliminados(prev => {
        if (!prev[id]) return prev;
        const copia = { ...prev };
        delete copia[id];
        return copia;
      });
    }
  };

  const actualizarAsistenciaAlumno = (id, asiste) => {
    setAlumnosAsisten(prev => ({
      ...prev,
      [id]: asiste,
    }));
  };

  // Contar alumnos que asisten a un turno (función de respaldo si el backend no devuelve el cálculo)
  const contarAlumnosQueAsisten = (alumnos) => {
    if (!alumnos || alumnos.length === 0) {
      return 0;
    }
    return alumnos.filter(alumno => {
      // Verificar si es alumno fijo o puntual
      const esFijo = alumno.tua_fijo === true || 
                     alumno.tua_fijo === 'true' || 
                     alumno.tua_fijo === 1 || 
                     alumno.tua_fijo === '1';
      
      // Si es alumno puntual (no fijo), siempre se cuenta como que asiste
      // porque si está en la lista filtrada para esa fecha, significa que está inscrito para asistir
      if (!esFijo) {
        return true;
      }
      
      // Para alumnos fijos, verificar el campo tua_asiste
      // Si tua_asiste no está definido, por defecto se considera que asiste (true)
      if (alumno.tua_asiste === undefined || alumno.tua_asiste === null) {
        return true;
      }
      // Convertir a booleano considerando diferentes formatos
      return alumno.tua_asiste === true || 
             alumno.tua_asiste === 'true' || 
             alumno.tua_asiste === 1 || 
             alumno.tua_asiste === '1';
    }).length;
  };

  // Renderizar bloque de turno que ocupa múltiples slots
  const renderBloqueTurno = (canchaId, horario) => {
    const turnosEnCancha = getTurnosPorDiaYCancha(canchaId);
    
    // Buscar el turno que comienza en este horario
    const turno = turnosEnCancha.find(t => esInicioTurno(horario, t));
    
    if (!turno) {
      return null;
    }

    // Filtrar alumnos según la fecha del día seleccionado
    const alumnosFiltrados = filtrarAlumnosPorFecha(turno);
    
    // Si no hay alumnos después del filtro, no mostrar el turno (o mostrar sin alumnos)
    // Por ahora, mostramos el turno aunque no tenga alumnos visibles en este día

    const esFijo = turno.tur_tipo === 'FIJO';
    const slotsOcupados = calcularSlotsTurno(turno);
    const baseSlotHeight = isMobile ? 34 : 32; // Aumentado de 24 a 32 para más altura en desktop
    
    // Usar el cálculo del backend si está disponible, sino calcular en el frontend
    const cantidadAsisten = turno.cantidad_alumnos_asisten !== undefined 
      ? turno.cantidad_alumnos_asisten 
      : contarAlumnosQueAsisten(alumnosFiltrados);
    // Usar la capacidad máxima del turno (tur_cantidad_alumnos)
    const capacidadMaxima = turno.tur_cantidad_alumnos || null;
    
    // Determinar color según categoría o tipo
    const getColor = () => {
      if (turno.categoria_color) {
        return turno.categoria_color;
      }
      return '#1890ff'; // Azul por defecto si no tiene categoría
    };

    // Altura: debe ocupar exactamente el espacio de los slots ocupados
    const alturaBloque = slotsOcupados * baseSlotHeight;
    const colorTurno = getColor();

    return (
      <div
        key={turno.tur_id}
        style={{
          position: 'absolute',
          top: '0px',
          left: '0px',
          right: '0px',
          bottom: isMobile ? '2px' : '4px',
          height: isMobile ? `${alturaBloque}px` : 'auto',
          background: isMobile ? colorTurno : '#2d2d2d',
          color: isMobile ? '#fff' : '#333',
          borderLeft: isMobile ? 'none' : `4px solid ${colorTurno}`,
          borderTop: isMobile ? 'none' : 'none',
          borderRight: isMobile ? 'none' : 'none',
          borderBottom: isMobile ? 'none' : 'none',
          padding: isMobile ? '4px 6px' : '6px 8px',
          borderRadius: isMobile ? '4px' : '0px',
          fontSize: isMobile ? '10px' : '11px',
          cursor: 'pointer',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          boxSizing: 'border-box',
          boxShadow: isMobile ? 'none' : 'none',
        }}
        onClick={() => handleEdit(turno)}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          width: '100%',
          marginBottom: isMobile ? '2px' : '3px'
        }}>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: isMobile ? '11px' : '12px', 
            lineHeight: '1.3',
            color: isMobile ? '#fff' : '#e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px',
            flex: 1
          }}>
            <TagsOutlined style={{ fontSize: isMobile ? '10px' : '11px', color: isMobile ? '#fff' : colorTurno }} />
            {turno.categoria_descripcion || 'Sin categoría'}
          </div>
          <Tag 
            size="small" 
            style={{ 
              fontSize: isMobile ? '8px' : '9px', 
              lineHeight: '1.2',
              margin: 0,
              flexShrink: 0
            }}
            color={esFijo ? 'green' : 'blue'}
          >
            {esFijo ? 'Fijo' : 'Evento'}
          </Tag>
        </div>
        {turno.profesor_nombre && (
          <div style={{ 
            fontSize: isMobile ? '9px' : '10px', 
            marginBottom: isMobile ? '1px' : '2px', 
            lineHeight: '1.3',
            color: isMobile ? '#fff' : '#b0b0b0',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px'
          }}>
            <UserOutlined style={{ fontSize: isMobile ? '8px' : '9px' }} />
            {turno.profesor_nombre} {turno.profesor_apellido}
          </div>
        )}
        <div style={{ 
          fontSize: isMobile ? '8px' : '9px', 
          marginTop: isMobile ? '1px' : '2px', 
          lineHeight: '1.3',
          color: isMobile ? '#fff' : '#b0b0b0',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '4px' : '6px'
        }}>
          <TeamOutlined style={{ fontSize: isMobile ? '7px' : '8px' }} />
          <span>
            {capacidadMaxima 
              ? `${cantidadAsisten} / ${capacidadMaxima} alumnos`
              : `${cantidadAsisten} alumno${cantidadAsisten !== 1 ? 's' : ''}`}
          </span>
        </div>
        {alumnosFiltrados && alumnosFiltrados.length > 0 && (
          <div style={{ 
            fontSize: isMobile ? '8px' : '9px', 
            marginTop: isMobile ? '1px' : '2px', 
            lineHeight: '1.3',
            color: isMobile ? '#fff' : '#b0b0b0',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '4px' : '6px'
          }}>
            <span style={{ fontSize: isMobile ? '7px' : '8px' }}>
              {alumnosFiltrados.slice(0, 2).map(a => `${a.alu_nombre} ${a.alu_apellido}`).join(', ')}
              {alumnosFiltrados.length > 2 && ` +${alumnosFiltrados.length - 2}`}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Verificar si una celda está ocupada por un turno que comenzó antes
  const estaOcupadaPorTurnoAnterior = (canchaId, horario) => {
    const turnosEnCancha = getTurnosPorDiaYCancha(canchaId);
    return turnosEnCancha.some(turno => {
      if (esInicioTurno(horario, turno)) {
        return false; // Esta es la celda de inicio
      }
      return estaEnTurno(horario, turno);
    });
  };

  // Columnas de la tabla
  const baseSlotHeight = isMobile ? 34 : 32; // Mismo valor que en renderBloqueTurno

  const horarioColumnWidth = isMobile ? 70 : 90;
  const canchasCount = Math.max(canchas.length, 1);
  let canchaColumnWidth = isMobile ? 150 : 160;

  if (!isMobile && tableWidth > 0) {
    const availableWidth = tableWidth - horarioColumnWidth - 24;
    if (availableWidth > 0) {
      const dynamicWidth = Math.floor(availableWidth / canchasCount);
      canchaColumnWidth = Math.max(120, dynamicWidth);
    }
  }

  const columns = [
    {
      title: 'HORARIO',
      dataIndex: 'horario',
      key: 'horario',
      width: horarioColumnWidth,
      fixed: 'left',
      render: (horario) => <strong>{horario}</strong>,
    },
    ...canchas.map(cancha => ({
      title: cancha.can_descripcion,
      key: `cancha_${cancha.can_id}`,
      width: canchaColumnWidth,
      render: (_, record) => {
        // Si esta celda está ocupada por un turno que comenzó antes, no renderizar nada
        if (estaOcupadaPorTurnoAnterior(cancha.can_id, record.horario)) {
          return {
            children: null,
            props: {
              rowSpan: 0,
            },
          };
        }

        const turnosEnCancha = getTurnosPorDiaYCancha(cancha.can_id);
        const turno = turnosEnCancha.find(t => esInicioTurno(record.horario, t));
        const slotsOcupados = turno ? calcularSlotsTurno(turno) : 0;
        
        return {
          children: (
            <div 
              style={{ 
                position: 'relative',
                minHeight: `${baseSlotHeight}px`,
                height: slotsOcupados > 0 ? `${slotsOcupados * baseSlotHeight}px` : `${baseSlotHeight}px`,
                border: '1px solid #404040', 
                padding: '0px',
                borderRadius: '0px',
                boxSizing: 'border-box',
              }}
            >
              {renderBloqueTurno(cancha.can_id, record.horario)}
            </div>
          ),
          props: {
            rowSpan: slotsOcupados > 0 ? slotsOcupados : 1,
          },
        };
      },
    })),
  ];

  const dataSource = horarios.map(horario => ({ key: horario, horario }));
  const inicioSemanaActual = obtenerInicioSemana();

  // Vista Mobile con Cards
  if (isMobile) {
  return (
    <MainLayout>
        <div className="agenda-container">
          <div className="agenda-header">
            <div className="agenda-header-info">
              <h1 style={{ color: '#ffffff' }}>Agenda / Calendario</h1>
              <div className="agenda-controls">
                <DatePicker
                  value={fechaSeleccionadaMobile}
                  onChange={(date) => {
                    setFechaSeleccionadaMobile(date || dayjs());
                    setCalendarOpen(false);
                  }}
                  format="DD/MM/YYYY"
                  open={calendarOpen}
                  onOpenChange={setCalendarOpen}
                  style={{ width: '100%', marginBottom: '12px' }}
                  suffixIcon={<CalendarOutlined />}
                  placeholder="Seleccionar fecha"
                  className="agenda-date-picker-button"
                />
              </div>
            </div>
            <div className="agenda-header-actions">
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', width: '100%' }}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setModalFiltroVisible(true)}
                  style={{ flex: '0 0 85%', height: '32px' }}
                >
                  Filtrar
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadTurnos}
                  loading={loading}
                  style={{ 
                    flex: '0 0 15%',
                    height: '32px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </div>
              {filtroCategoria && (
                <div style={{ marginBottom: '8px', width: '100%' }}>
                  <Tag 
                    closable 
                    onClose={() => setFiltroCategoria(null)}
                    color="blue"
                    style={{ width: '100%', padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {categorias.find(c => c.cat_id === filtroCategoria)?.cat_descripcion || 'Categoría'}
                  </Tag>
                </div>
              )}
              <Button
                icon={<BellOutlined />}
                onClick={handleAvisoProfesores}
                block
                style={{ marginBottom: '8px' }}
              >
                Aviso a Profesores
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                block
              >
                Nuevo Turno
              </Button>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <Spin spinning={loadingInitial || loadingTurnos} tip={loadingInitial ? "Cargando datos..." : "Cargando turnos..."}>
              {loadingTurnos ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  Cargando turnos...
                </div>
              ) : turnosMobile.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                  No hay turnos para esta fecha
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {turnosMobile.map(turno => {
                  const cancha = canchas.find(c => c.can_id === turno.tur_cancha);
                  const esFijo = turno.tur_tipo === 'FIJO';
                  const getColor = () => {
                    if (turno.categoria_color) {
                      return turno.categoria_color;
                    }
                    return '#1890ff'; // Azul por defecto si no tiene categoría
                  };
                  
                  // Usar el cálculo del backend si está disponible, sino calcular en el frontend
                  const cantidadAsisten = turno.cantidad_alumnos_asisten !== undefined 
                    ? turno.cantidad_alumnos_asisten 
                    : contarAlumnosQueAsisten(turno.alumnosFiltrados || []);
                  // Usar la capacidad máxima del turno (tur_cantidad_alumnos)
                  const capacidadMaxima = turno.tur_cantidad_alumnos || null;

                  return (
                    <Card
                      key={turno.tur_id}
                      hoverable
                      onClick={() => handleEdit(turno)}
                      style={{
                        borderLeft: `4px solid ${getColor()}`,
                        borderRadius: '8px',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <TagsOutlined style={{ color: getColor() }} />
                              {turno.categoria_descripcion || 'Sin categoría'}
                            </div>
                            <div style={{ fontSize: '14px', color: '#b0b0b0', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <EnvironmentOutlined />
                              {cancha?.can_descripcion || 'Cancha no encontrada'}
                            </div>
                            <div style={{ fontSize: '13px', color: '#b0b0b0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <ClockCircleOutlined />
                              {turno.tur_hora_desde} - {turno.tur_hora_hasta}
                            </div>
                          </div>
                          <Tag color={esFijo ? 'green' : 'blue'}>
                            {esFijo ? 'Fijo' : 'Evento'}
                          </Tag>
                        </div>
                        {turno.profesor_nombre && (
                          <div style={{ fontSize: '13px', color: '#b0b0b0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <UserOutlined />
                            <span>Profesor: {turno.profesor_nombre} {turno.profesor_apellido}</span>
                          </div>
                        )}
                        <div style={{ fontSize: '13px', color: '#b0b0b0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TeamOutlined />
                          <span>
                            {capacidadMaxima 
                              ? `${cantidadAsisten} / ${capacidadMaxima} alumnos`
                              : `${cantidadAsisten} alumno${cantidadAsisten !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                        {turno.alumnosFiltrados && turno.alumnosFiltrados.length > 0 && (
                          <div style={{ fontSize: '13px', color: '#b0b0b0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>
                              {turno.alumnosFiltrados.map(a => `${a.alu_nombre} ${a.alu_apellido}`).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
                </div>
              )}
            </Spin>
          </div>
        </div>

        {/* Modal para filtrar por categoría */}
        <Modal
          title="Filtrar por Categoría"
          open={modalFiltroVisible}
          onOk={() => setModalFiltroVisible(false)}
          onCancel={() => setModalFiltroVisible(false)}
          okText="Aplicar"
          cancelText="Cancelar"
        >
          <Select
            placeholder="Seleccione una categoría"
            allowClear
            value={filtroCategoria}
            onChange={(value) => {
              setFiltroCategoria(value);
              setModalFiltroVisible(false);
            }}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {categorias.map(cat => (
              <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                {cat.cat_descripcion}
              </Select.Option>
            ))}
          </Select>
        </Modal>

        {/* Modal para crear/editar turno - Mobile */}
        <Modal
          title={editingTurno ? 'Editar Turno' : 'Nuevo Turno'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '95%' : 700}
          className="agenda-turno-modal"
          style={isMobile ? { top: 20, maxHeight: '90vh' } : {}}
          bodyStyle={isMobile ? { maxHeight: 'calc(90vh - 110px)', overflowY: 'auto', padding: '16px' } : {}}
          confirmLoading={loadingSave}
          footer={[
            <Button 
              key="share" 
              icon={<ShareAltOutlined />}
              onClick={handleCompartirDisponibilidad}
              style={{ marginRight: '8px' }}
            >
              Compartir Disponibilidad
            </Button>,
            <Button key="cancel" onClick={() => {
              setModalVisible(false);
              form.resetFields();
            }} style={{ marginRight: '8px' }}>
              Cancelar
            </Button>,
            <Button key="submit" type="primary" onClick={handleSave} loading={loadingSave}>
              Guardar
            </Button>,
          ]}
        >
          <Spin spinning={loadingEdit} tip="Cargando turno...">
            <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="tipo"
              label="Tipo de Turno"
              rules={[{ required: true, message: 'El tipo es requerido' }]}
            >
              <Radio.Group style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '8px' } : {}}>
                <Radio value="FIJO">Fijo (Recurrente)</Radio>
                <Radio value="EVENTO">Evento (Único)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
            >
              {({ getFieldValue }) => {
                const tipo = getFieldValue('tipo');
                return tipo === 'FIJO' ? (
                  <Form.Item
                    name="diaSemana"
                    label="Día de la Semana"
                    rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                  >
                    <Select placeholder="Seleccione el día">
                      {diasSemana.map(dia => (
                        <Select.Option key={dia.key} value={dia.key}>
                          {dia.nombre}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="fechaEspecifica"
                    label="Fecha Específica"
                    rules={[{ required: true, message: 'La fecha es requerida' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder="Seleccione la fecha"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Row gutter={isMobile ? 0 : 16}>
              <Col span={isMobile ? 24 : 12}>
                <Form.Item
                  name="horaDesde"
                  label="Hora Desde"
                  rules={[{ required: true, message: 'La hora desde es requerida' }]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Hora inicio"
                  />
                </Form.Item>
              </Col>
              <Col span={isMobile ? 24 : 12}>
                <Form.Item
                  name="horaHasta"
                  label="Hora Hasta"
                  rules={[{ required: true, message: 'La hora hasta es requerida' }]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Hora fin"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="cancha"
              label="Cancha"
              rules={[{ required: true, message: 'La cancha es requerida' }]}
            >
              <Select placeholder="Seleccione la cancha" loading={loading}>
                {canchas.length > 0 ? (
                  canchas.map(cancha => (
                    <Select.Option key={cancha.can_id} value={cancha.can_id}>
                      {cancha.can_descripcion}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay canchas disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="categoria"
              label="Categoría"
            >
              <Select placeholder="Seleccione la categoría" allowClear loading={loading}>
                {categorias.length > 0 ? (
                  categorias.map(cat => (
                    <Select.Option key={cat.cat_id} value={cat.cat_id}>
                      {cat.cat_descripcion}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay categorías disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="profesor"
              label="Profesor"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const tipo = getFieldValue('tipo');
                    // Para turnos fijos nuevos, el profesor NO es requerido (se asignará por fecha)
                    // Para turnos fijos editados o eventos, el profesor es requerido
                    if (tipo === 'FIJO' && !editingTurno) {
                      return Promise.resolve();
                    }
                    if (!value) {
                      return Promise.reject(new Error('El profesor es requerido'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select placeholder="Seleccione el profesor" allowClear showSearch loading={loading}>
                {profesores.length > 0 ? (
                  profesores.map(prof => (
                    <Select.Option key={prof.pro_id} value={prof.pro_id}>
                      {prof.pro_nombre} {prof.pro_apellido}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay profesores disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="alumnos"
              label="Alumnos"
            >
              <Select
                mode="multiple"
                placeholder="Seleccione los alumnos"
                showSearch
                loading={loading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(selectedIds) => {
                  // Inicializar alumnos fijos para los nuevos seleccionados
                  const nuevosFijos = { ...alumnosFijos };
                  const nuevosAsisten = { ...alumnosAsisten };
                  selectedIds.forEach(id => {
                    if (nuevosFijos[id] === undefined) {
                      // Por defecto, si es turno FIJO, el alumno es fijo; si es EVENTO, no es fijo
                      const tipo = form.getFieldValue('tipo');
                      nuevosFijos[id] = tipo === 'FIJO';
                    }
                    // Por defecto, todos los alumnos asisten
                    if (nuevosAsisten[id] === undefined) {
                      nuevosAsisten[id] = true;
                    }
                  });
                  // Eliminar los que ya no están seleccionados
                  Object.keys(nuevosFijos).forEach(id => {
                    if (!selectedIds.includes(parseInt(id))) {
                      delete nuevosFijos[id];
                    }
                  });
                  Object.keys(nuevosAsisten).forEach(id => {
                    if (!selectedIds.includes(parseInt(id))) {
                      delete nuevosAsisten[id];
                    }
                  });
                  setAlumnosFijos(nuevosFijos);
                  setAlumnosAsisten(nuevosAsisten);
                }}
              >
                {alumnos.length > 0 ? (
                  alumnos.map(alumno => (
                    <Select.Option 
                      key={alumno.alu_id} 
                      value={alumno.alu_id}
                      label={`${alumno.alu_nombre} ${alumno.alu_apellido}`}
                    >
                      {alumno.alu_nombre} {alumno.alu_apellido}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay alumnos disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            {/* Lista de alumnos seleccionados con opción de marcar como fijo/no fijo */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.alumnos !== currentValues.alumnos || prevValues.tipo !== currentValues.tipo
              }
            >
              {({ getFieldValue }) => {
                const alumnosSeleccionados = getFieldValue('alumnos') || [];
                const tipoTurno = getFieldValue('tipo');
                
                if (alumnosSeleccionados.length === 0) {
                  return null;
                }

                const alumnosDetalle = alumnosSeleccionados
                  .map(id => {
                    const alumno = alumnos.find(a => a.alu_id === id);
                    if (!alumno) return null;
                    const esFijo = alumnosFijos[id] !== undefined ? alumnosFijos[id] : tipoTurno === 'FIJO';
                    const asiste = alumnosAsisten[id] !== undefined ? alumnosAsisten[id] : true;
                    return { ...alumno, id, esFijo, asiste };
                  })
                  .filter(Boolean);

                if (tipoTurno !== 'FIJO') {
                  return (
                    <Form.Item label="Configuración de Alumnos">
                      <List
                        size="small"
                        bordered
                        dataSource={alumnosDetalle}
                        renderItem={(item) => (
                          <List.Item
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              flexWrap: isMobile ? 'wrap' : 'nowrap',
                              gap: isMobile ? '8px' : '0'
                            }}
                          >
                            <span style={isMobile ? { width: '100%', marginBottom: isMobile ? '8px' : '0' } : {}}>
                              {item.alu_nombre} {item.alu_apellido}
                            </span>
                            <Space wrap={isMobile}>
                              <Switch
                                checked={item.asiste}
                                checkedChildren="Asiste"
                                unCheckedChildren="No asiste"
                                onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                              />
                              <Button
                                size="small"
                                danger
                                onClick={() => quitarAlumnoDeSeleccion(item.id)}
                              >
                                Quitar
                              </Button>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Form.Item>
                  );
                }

                const alumnosFijosSeleccionados = alumnosDetalle.filter(a => a.esFijo);
                const alumnosPuntualesSeleccionados = alumnosDetalle.filter(a => !a.esFijo);
                const fechaActualTexto = getFechaDiaSeleccionado().format('DD/MM/YYYY');

                const renderAlumnoItem = (item, { esFijo }) => (
                  <div
                    key={item.id}
                    style={{
                      border: '1px solid #404040',
                      borderRadius: 8,
                      padding: isMobile ? '10px 12px' : '12px 16px',
                      marginBottom: 12,
                      background: '#2d2d2d',
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: isMobile ? 'flex-start' : 'center', 
                      marginBottom: 8,
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '8px' : '0'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: isMobile ? '14px' : '16px' }}>
                          {item.alu_nombre} {item.alu_apellido}
                        </div>
                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#b0b0b0' }}>
                          {esFijo ? 'Asiste todas las semanas' : `Alumno puntual para el ${fechaActualTexto}`}
                        </div>
                      </div>
                      {esFijo && (
                        <Switch
                          size="small"
                          checked={item.asiste}
                          checkedChildren="Asiste"
                          unCheckedChildren="No asiste"
                          onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                        />
                      )}
                    </div>
                    <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                      {esFijo ? (
                        <>
                          <Button
                            size="small"
                            onClick={() => marcarAlumnoComoFijo(item.id, false)}
                            block={isMobile}
                          >
                            Pasar a puntual
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={() => quitarAlumnoDeSeleccion(item.id, { eliminarFijo: true })}
                            block={isMobile}
                          >
                            Eliminar del turno
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            danger
                            onClick={() => quitarAlumnoDeSeleccion(item.id)}
                            block={isMobile}
                          >
                            Quitar de esta fecha
                          </Button>
                          <Button
                            size="small"
                            onClick={() => marcarAlumnoComoFijo(item.id, true)}
                            block={isMobile}
                          >
                            Convertir en fijo
                          </Button>
                        </>
                      )}
                    </Space>
                  </div>
                );

                return (
                  <Form.Item label="Configuración de Alumnos">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Alumnos fijos</div>
                        {alumnosFijosSeleccionados.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#888' }}>No hay alumnos fijos seleccionados.</div>
                        ) : (
                          alumnosFijosSeleccionados.map(item => renderAlumnoItem(item, { esFijo: true }))
                        )}
                      </div>

                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Alumnos puntuales</div>
                        {alumnosPuntualesSeleccionados.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#888' }}>No hay alumnos puntuales para este día.</div>
                        ) : (
                          alumnosPuntualesSeleccionados.map(item => renderAlumnoItem(item, { esFijo: false }))
                        )}
                      </div>

                      <div style={{ fontSize: 12, color: '#b0b0b0' }}>
                        <strong>Nota:</strong> Los alumnos fijos tienen control de asistencia por fecha. Los puntuales se quitan del turno actual con "Quitar de esta fecha".
                      </div>
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
          </Spin>
        </Modal>
      </MainLayout>
    );
  }

  // Vista Desktop con Calendario
  return (
    <MainLayout>
      <div className="agenda-container">
        <div className="agenda-header">
          <div className="agenda-header-info">
            <h1>Agenda / Calendario</h1>
            <div className="agenda-controls">
              <Button icon={<LeftOutlined />} onClick={semanaAnterior}>
                Semana Anterior
              </Button>
              <Button onClick={irAHoy}>
                Hoy
              </Button>
              <Button icon={<RightOutlined />} onClick={semanaSiguiente}>
                Semana Siguiente
              </Button>
              <span className="agenda-week-info">
                {getFechaDiaSeleccionado().format('DD/MM/YYYY')}
              </span>
          </div>
          </div>
          <div className="agenda-header-actions">
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setModalFiltroVisible(true)}
              >
                Filtrar
              </Button>
              {filtroCategoria && (
                <Tag 
                  closable 
                  onClose={() => setFiltroCategoria(null)}
                  color="blue"
                >
                  {categorias.find(c => c.cat_id === filtroCategoria)?.cat_descripcion || 'Categoría'}
                </Tag>
              )}
              <Button
                icon={<BellOutlined />}
                onClick={handleAvisoProfesores}
              >
                Aviso a Profesores
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadTurnos}
                loading={loading}
              >
                Actualizar
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Nuevo Turno
              </Button>
            </Space>
          </div>
        </div>

        <Spin spinning={loadingInitial || loadingTurnos} tip={loadingInitial ? "Cargando datos..." : "Cargando turnos..."}>
          <div className="agenda-table-wrapper" ref={tableWrapperRef}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={false}
              scroll={isMobile ? { x: 'max-content' } : undefined}
              size={isMobile ? 'middle' : 'small'}
          />
        </div>
        </Spin>
      </div>

      {/* Modal para filtrar por categoría */}
      <Modal
        title="Filtrar por Categoría"
        open={modalFiltroVisible}
        onOk={() => setModalFiltroVisible(false)}
        onCancel={() => setModalFiltroVisible(false)}
        okText="Aplicar"
        cancelText="Cancelar"
      >
        <Select
          placeholder="Seleccione una categoría"
          allowClear
          value={filtroCategoria}
          onChange={(value) => {
            setFiltroCategoria(value);
            setModalFiltroVisible(false);
          }}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {categorias.map(cat => (
            <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
              {cat.cat_descripcion}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Selector de días de la semana - Fijo en la parte inferior */}
      <div className="agenda-day-selector">
        {diasSemana.map(dia => {
          const fechaDia = inicioSemanaActual.add(dia.key - 1, 'day');
          const estaSeleccionado = diaSeleccionado === dia.key;
          return (
            <Button
              key={dia.key}
              type={estaSeleccionado ? 'primary' : 'default'}
              onClick={() => setDiaSeleccionado(dia.key)}
              className={`agenda-day-button${estaSeleccionado ? ' agenda-day-button--active' : ''}`}
            >
              <div className="agenda-day-button__content">
                <div>{dia.nombre.toUpperCase()}</div>
                <div className="agenda-day-button__date">
                  {fechaDia.format('DD/MM')}
                </div>
              </div>
            </Button>
          );
        })}
      </div>

      {/* Modal para crear/editar turno */}
      <Modal
          title={editingTurno ? 'Editar Turno' : 'Nuevo Turno'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '95%' : 1200}
          className="agenda-turno-modal"
          style={isMobile ? { top: 20, maxHeight: '90vh' } : {}}
          bodyStyle={isMobile ? { maxHeight: 'calc(90vh - 110px)', overflowY: 'auto', padding: '16px' } : { padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}
          confirmLoading={loadingSave}
          footer={[
            <Button 
              key="share" 
              icon={<ShareAltOutlined />}
              onClick={handleCompartirDisponibilidad}
              style={{ float: 'left' }}
            >
              Compartir Disponibilidad
            </Button>,
            <Button key="cancel" onClick={() => {
              setModalVisible(false);
              form.resetFields();
            }}>
              Cancelar
            </Button>,
            <Button key="submit" type="primary" onClick={handleSave} loading={loadingSave}>
              Guardar
            </Button>,
          ]}
        >
          <Spin spinning={loadingEdit} tip="Cargando turno...">
          <Form
            form={form}
            layout="vertical"
          >
            {isMobile ? (
              // Vista Mobile - Layout vertical completo
              <>
                <Form.Item
                  name="tipo"
                  label="Tipo de Turno"
                  rules={[{ required: true, message: 'El tipo es requerido' }]}
                >
                  <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Radio value="FIJO">Fijo (Recurrente)</Radio>
                    <Radio value="EVENTO">Evento (Único)</Radio>
                  </Radio.Group>
                </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
            >
              {({ getFieldValue }) => {
                const tipo = getFieldValue('tipo');
                return tipo === 'FIJO' ? (
                  <Form.Item
                    name="diaSemana"
                    label="Día de la Semana"
                    rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                  >
                    <Select placeholder="Seleccione el día">
                      {diasSemana.map(dia => (
                        <Select.Option key={dia.key} value={dia.key}>
                          {dia.nombre}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="fechaEspecifica"
                    label="Fecha Específica"
                    rules={[{ required: true, message: 'La fecha es requerida' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder="Seleccione la fecha"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Row gutter={isMobile ? 0 : 16}>
              <Col span={isMobile ? 24 : 12}>
                <Form.Item
                  name="horaDesde"
                  label="Hora Desde"
                  rules={[{ required: true, message: 'La hora desde es requerida' }]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Hora inicio"
                  />
                </Form.Item>
              </Col>
              <Col span={isMobile ? 24 : 12}>
                <Form.Item
                  name="horaHasta"
                  label="Hora Hasta"
                  rules={[{ required: true, message: 'La hora hasta es requerida' }]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    placeholder="Hora fin"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="cancha"
              label="Cancha"
              rules={[{ required: true, message: 'La cancha es requerida' }]}
            >
              <Select placeholder="Seleccione la cancha" loading={loading}>
                {canchas.length > 0 ? (
                  canchas.map(cancha => (
                    <Select.Option key={cancha.can_id} value={cancha.can_id}>
                      {cancha.can_descripcion}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay canchas disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="categoria"
              label="Categoría"
            >
              <Select placeholder="Seleccione la categoría" allowClear loading={loading}>
                {categorias.length > 0 ? (
                  categorias.map(cat => (
                    <Select.Option key={cat.cat_id} value={cat.cat_id}>
                      {cat.cat_descripcion}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay categorías disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="profesor"
              label="Profesor"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const tipo = getFieldValue('tipo');
                    // Para turnos fijos nuevos, el profesor NO es requerido (se asignará por fecha)
                    // Para turnos fijos editados o eventos, el profesor es requerido
                    if (tipo === 'FIJO' && !editingTurno) {
                      return Promise.resolve();
                    }
                    if (!value) {
                      return Promise.reject(new Error('El profesor es requerido'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select placeholder="Seleccione el profesor" allowClear showSearch loading={loading}>
                {profesores.length > 0 ? (
                  profesores.map(prof => (
                    <Select.Option key={prof.pro_id} value={prof.pro_id}>
                      {prof.pro_nombre} {prof.pro_apellido}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay profesores disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="cantidadAlumnos"
              label="Cantidad de Alumnos (Máximo)"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Cantidad máxima de alumnos"
                min={1}
                max={100}
              />
            </Form.Item>

            <Form.Item
              name="alumnos"
              label="Alumnos"
            >
              <Select
                mode="multiple"
                placeholder="Seleccione los alumnos"
                showSearch
                loading={loading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(selectedIds) => {
                  // Inicializar alumnos fijos para los nuevos seleccionados
                  const nuevosFijos = { ...alumnosFijos };
                  const nuevosAsisten = { ...alumnosAsisten };
                  selectedIds.forEach(id => {
                    if (nuevosFijos[id] === undefined) {
                      // Por defecto, si es turno FIJO, el alumno es fijo; si es EVENTO, no es fijo
                      const tipo = form.getFieldValue('tipo');
                      nuevosFijos[id] = tipo === 'FIJO';
                    }
                    // Por defecto, todos los alumnos asisten
                    if (nuevosAsisten[id] === undefined) {
                      nuevosAsisten[id] = true;
                    }
                  });
                  // Eliminar los que ya no están seleccionados
                  Object.keys(nuevosFijos).forEach(id => {
                    if (!selectedIds.includes(parseInt(id))) {
                      delete nuevosFijos[id];
                    }
                  });
                  Object.keys(nuevosAsisten).forEach(id => {
                    if (!selectedIds.includes(parseInt(id))) {
                      delete nuevosAsisten[id];
                    }
                  });
                  setAlumnosFijos(nuevosFijos);
                  setAlumnosAsisten(nuevosAsisten);
                }}
              >
                {alumnos.length > 0 ? (
                  alumnos.map(alumno => (
                    <Select.Option 
                      key={alumno.alu_id} 
                      value={alumno.alu_id}
                      label={`${alumno.alu_nombre} ${alumno.alu_apellido}`}
                    >
                      {alumno.alu_nombre} {alumno.alu_apellido}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option disabled value="">No hay alumnos disponibles</Select.Option>
                )}
              </Select>
            </Form.Item>

            {/* Lista de alumnos seleccionados con opción de marcar como fijo/no fijo */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.alumnos !== currentValues.alumnos || prevValues.tipo !== currentValues.tipo
              }
            >
              {({ getFieldValue }) => {
                const alumnosSeleccionados = getFieldValue('alumnos') || [];
                const tipoTurno = getFieldValue('tipo');
                
                if (alumnosSeleccionados.length === 0) {
                  return null;
                }

                const alumnosDetalle = alumnosSeleccionados
                  .map(id => {
                    const alumno = alumnos.find(a => a.alu_id === id);
                    if (!alumno) return null;
                    const esFijo = alumnosFijos[id] !== undefined ? alumnosFijos[id] : tipoTurno === 'FIJO';
                    const asiste = alumnosAsisten[id] !== undefined ? alumnosAsisten[id] : true;
                    return { ...alumno, id, esFijo, asiste };
                  })
                  .filter(Boolean);

                if (tipoTurno !== 'FIJO') {
                  return (
                    <Form.Item label="Configuración de Alumnos">
                      <List
                        size="small"
                        bordered
                        dataSource={alumnosDetalle}
                        renderItem={(item) => (
                          <List.Item
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              flexWrap: isMobile ? 'wrap' : 'nowrap',
                              gap: isMobile ? '8px' : '0'
                            }}
                          >
                            <span style={isMobile ? { width: '100%', marginBottom: isMobile ? '8px' : '0' } : {}}>
                              {item.alu_nombre} {item.alu_apellido}
                            </span>
                            <Space wrap={isMobile}>
                              <Switch
                                checked={item.asiste}
                                checkedChildren="Asiste"
                                unCheckedChildren="No asiste"
                                onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                              />
                              <Button
                                size="small"
                                danger
                                onClick={() => quitarAlumnoDeSeleccion(item.id)}
                              >
                                Quitar
                              </Button>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Form.Item>
                  );
                }

                const alumnosFijosSeleccionados = alumnosDetalle.filter(a => a.esFijo);
                const alumnosPuntualesSeleccionados = alumnosDetalle.filter(a => !a.esFijo);
                const fechaActualTexto = getFechaDiaSeleccionado().format('DD/MM/YYYY');

                const renderAlumnoItem = (item, { esFijo }) => (
                  <div
                    key={item.id}
                    className="alumno-card"
                    style={{
                      border: '1px solid #404040',
                      borderRadius: 8,
                      padding: isMobile ? '14px 16px' : '16px 20px',
                      marginBottom: 12,
                      background: '#2d2d2d',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: isMobile ? 'flex-start' : 'center', 
                      marginBottom: 8,
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '8px' : '0'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>
                          {item.alu_nombre} {item.alu_apellido}
                        </div>
                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#b0b0b0', marginTop: 4 }}>
                          {esFijo ? 'Asiste todas las semanas' : `Alumno puntual para el ${fechaActualTexto}`}
                        </div>
                      </div>
                      {esFijo && (
                        <Switch
                          size="small"
                          checked={item.asiste}
                          checkedChildren="Asiste"
                          unCheckedChildren="No asiste"
                          onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                        />
                      )}
                    </div>
                    <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                      {esFijo ? (
                        <>
                          <Button
                            size="small"
                            onClick={() => marcarAlumnoComoFijo(item.id, false)}
                            block={isMobile}
                          >
                            Pasar a puntual
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={() => quitarAlumnoDeSeleccion(item.id, { eliminarFijo: true })}
                            block={isMobile}
                          >
                            Eliminar del turno
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            danger
                            onClick={() => quitarAlumnoDeSeleccion(item.id)}
                            block={isMobile}
                          >
                            Quitar de esta fecha
                          </Button>
                          <Button
                            size="small"
                            onClick={() => marcarAlumnoComoFijo(item.id, true)}
                            block={isMobile}
                          >
                            Convertir en fijo
                          </Button>
                        </>
                      )}
                    </Space>
                  </div>
                );

                return (
                  <Form.Item label="Configuración de Alumnos">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: 15,
                          marginBottom: 12,
                          paddingBottom: 8,
                          borderBottom: '1px solid #404040',
                          color: '#e0e0e0'
                        }}>
                          Alumnos fijos
                        </div>
                        {alumnosFijosSeleccionados.length === 0 ? (
                          <div style={{ 
                            fontSize: 12, 
                            color: '#888',
                            fontStyle: 'italic',
                            padding: '12px',
                            textAlign: 'center',
                            background: '#252525',
                            borderRadius: 6,
                            border: '1px dashed #404040'
                          }}>
                            No hay alumnos fijos seleccionados.
                          </div>
                        ) : (
                          alumnosFijosSeleccionados.map(item => renderAlumnoItem(item, { esFijo: true }))
                        )}
                      </div>

                      <div>
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: 15,
                          marginBottom: 12,
                          paddingBottom: 8,
                          borderBottom: '1px solid #404040',
                          color: '#e0e0e0'
                        }}>
                          Alumnos puntuales
                        </div>
                        {alumnosPuntualesSeleccionados.length === 0 ? (
                          <div style={{ 
                            fontSize: 12, 
                            color: '#888',
                            fontStyle: 'italic',
                            padding: '12px',
                            textAlign: 'center',
                            background: '#252525',
                            borderRadius: 6,
                            border: '1px dashed #404040'
                          }}>
                            No hay alumnos puntuales para este día.
                          </div>
                        ) : (
                          alumnosPuntualesSeleccionados.map(item => renderAlumnoItem(item, { esFijo: false }))
                        )}
                      </div>

                      <div style={{ fontSize: 12, color: '#b0b0b0' }}>
                        <strong>Nota:</strong> Los alumnos fijos tienen control de asistencia por fecha. Los puntuales se quitan del turno actual con "Quitar de esta fecha".
                      </div>
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>

                <Form.Item
                  name="activo"
                  label="Estado"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                </Form.Item>
              </>
            ) : (
              // Vista Desktop - Layout de dos columnas
              <Row gutter={24}>
                {/* Columna Izquierda - Campos del Formulario */}
                <Col span={14}>
                  <Form.Item
                    name="tipo"
                    label="Tipo de Turno"
                    rules={[{ required: true, message: 'El tipo es requerido' }]}
                  >
                    <Radio.Group>
                      <Radio value="FIJO">Fijo (Recurrente)</Radio>
                      <Radio value="EVENTO">Evento (Único)</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
                  >
                    {({ getFieldValue }) => {
                      const tipo = getFieldValue('tipo');
                      return tipo === 'FIJO' ? (
                        <Form.Item
                          name="diaSemana"
                          label="Día de la Semana"
                          rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                        >
                          <Select placeholder="Seleccione el día">
                            {diasSemana.map(dia => (
                              <Select.Option key={dia.key} value={dia.key}>
                                {dia.nombre}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="fechaEspecifica"
                          label="Fecha Específica"
                          rules={[{ required: true, message: 'La fecha es requerida' }]}
                        >
                          <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Seleccione la fecha"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="horaDesde"
                        label="Hora Desde"
                        rules={[{ required: true, message: 'La hora desde es requerida' }]}
                      >
                        <TimePicker
                          style={{ width: '100%' }}
                          format="HH:mm"
                          placeholder="Hora inicio"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="horaHasta"
                        label="Hora Hasta"
                        rules={[{ required: true, message: 'La hora hasta es requerida' }]}
                      >
                        <TimePicker
                          style={{ width: '100%' }}
                          format="HH:mm"
                          placeholder="Hora fin"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="cancha"
                    label="Cancha"
                    rules={[{ required: true, message: 'La cancha es requerida' }]}
                  >
                    <Select placeholder="Seleccione la cancha" loading={loading}>
                      {canchas.length > 0 ? (
                        canchas.map(cancha => (
                          <Select.Option key={cancha.can_id} value={cancha.can_id}>
                            {cancha.can_descripcion}
                          </Select.Option>
                        ))
                      ) : (
                        <Select.Option disabled value="">No hay canchas disponibles</Select.Option>
                      )}
                    </Select>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="categoria"
                        label="Categoría"
                      >
                        <Select placeholder="Seleccione la categoría" allowClear loading={loading}>
                          {categorias.length > 0 ? (
                            categorias.map(cat => (
                              <Select.Option key={cat.cat_id} value={cat.cat_id}>
                                {cat.cat_descripcion}
                              </Select.Option>
                            ))
                          ) : (
                            <Select.Option disabled value="">No hay categorías disponibles</Select.Option>
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="profesor"
                        label="Profesor"
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const tipo = getFieldValue('tipo');
                              if (tipo === 'FIJO' && !editingTurno) {
                                return Promise.resolve();
                              }
                              if (!value) {
                                return Promise.reject(new Error('El profesor es requerido'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select placeholder="Seleccione el profesor" allowClear showSearch loading={loading}>
                          {profesores.length > 0 ? (
                            profesores.map(prof => (
                              <Select.Option key={prof.pro_id} value={prof.pro_id}>
                                {prof.pro_nombre} {prof.pro_apellido}
                              </Select.Option>
                            ))
                          ) : (
                            <Select.Option disabled value="">No hay profesores disponibles</Select.Option>
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="cantidadAlumnos"
                    label="Cantidad de Alumnos (Máximo)"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Cantidad máxima de alumnos"
                      min={1}
                      max={100}
                    />
                  </Form.Item>

                  <Form.Item
                    name="alumnos"
                    label="Alumnos"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Seleccione los alumnos"
                      showSearch
                      loading={loading}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(selectedIds) => {
                        const nuevosFijos = { ...alumnosFijos };
                        const nuevosAsisten = { ...alumnosAsisten };
                        selectedIds.forEach(id => {
                          if (nuevosFijos[id] === undefined) {
                            const tipo = form.getFieldValue('tipo');
                            nuevosFijos[id] = tipo === 'FIJO';
                          }
                          if (nuevosAsisten[id] === undefined) {
                            nuevosAsisten[id] = true;
                          }
                        });
                        Object.keys(nuevosFijos).forEach(id => {
                          if (!selectedIds.includes(parseInt(id))) {
                            delete nuevosFijos[id];
                          }
                        });
                        Object.keys(nuevosAsisten).forEach(id => {
                          if (!selectedIds.includes(parseInt(id))) {
                            delete nuevosAsisten[id];
                          }
                        });
                        setAlumnosFijos(nuevosFijos);
                        setAlumnosAsisten(nuevosAsisten);
                      }}
                    >
                      {alumnos.length > 0 ? (
                        alumnos.map(alumno => (
                          <Select.Option 
                            key={alumno.alu_id} 
                            value={alumno.alu_id}
                            label={`${alumno.alu_nombre} ${alumno.alu_apellido}`}
                          >
                            {alumno.alu_nombre} {alumno.alu_apellido}
                          </Select.Option>
                        ))
                      ) : (
                        <Select.Option disabled value="">No hay alumnos disponibles</Select.Option>
                      )}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="activo"
                    label="Estado"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                  </Form.Item>
                </Col>

                {/* Columna Derecha - Configuración de Alumnos */}
                <Col span={10}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => 
                      prevValues.alumnos !== currentValues.alumnos || prevValues.tipo !== currentValues.tipo
                    }
                  >
                    {({ getFieldValue }) => {
                      const alumnosSeleccionados = getFieldValue('alumnos') || [];
                      const tipoTurno = getFieldValue('tipo');
                      
                      if (alumnosSeleccionados.length === 0) {
                        return (
                          <div style={{ 
                            padding: '20px',
                            textAlign: 'center',
                            color: '#888',
                            fontSize: '14px',
                            background: '#252525',
                            borderRadius: 8,
                            border: '1px dashed #404040'
                          }}>
                            Seleccione alumnos para configurarlos
                          </div>
                        );
                      }

                      const alumnosDetalle = alumnosSeleccionados
                        .map(id => {
                          const alumno = alumnos.find(a => a.alu_id === id);
                          if (!alumno) return null;
                          const esFijo = alumnosFijos[id] !== undefined ? alumnosFijos[id] : tipoTurno === 'FIJO';
                          const asiste = alumnosAsisten[id] !== undefined ? alumnosAsisten[id] : true;
                          return { ...alumno, id, esFijo, asiste };
                        })
                        .filter(Boolean);

                      if (tipoTurno !== 'FIJO') {
                        return (
                          <div>
                            <div style={{ 
                              fontWeight: 600, 
                              fontSize: 16,
                              marginBottom: 16,
                              paddingBottom: 8,
                              borderBottom: '1px solid #404040',
                              color: '#e0e0e0'
                            }}>
                              Configuración de Alumnos
                            </div>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '12px',
                              maxHeight: 'calc(80vh - 200px)',
                              overflowY: 'auto',
                              paddingRight: '8px'
                            }}>
                              {alumnosDetalle.map(item => (
                                <div
                                  key={item.id}
                                  className="alumno-card-compact"
                                  style={{
                                    border: '1px solid #404040',
                                    borderRadius: 8,
                                    padding: '12px',
                                    background: '#2d2d2d',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    minHeight: '120px'
                                  }}
                                >
                                  <div style={{ fontWeight: 600, fontSize: '13px', color: '#ffffff' }}>
                                    {item.alu_nombre} {item.alu_apellido}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#b0b0b0' }}>
                                    Alumno puntual
                                  </div>
                                  <Space size="small" style={{ marginTop: 'auto' }}>
                                    <Switch
                                      size="small"
                                      checked={item.asiste}
                                      checkedChildren="✓"
                                      unCheckedChildren="✗"
                                      onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                                    />
                                    <Button
                                      size="small"
                                      danger
                                      onClick={() => quitarAlumnoDeSeleccion(item.id)}
                                    >
                                      Quitar
                                    </Button>
                                  </Space>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      const alumnosFijosSeleccionados = alumnosDetalle.filter(a => a.esFijo);
                      const alumnosPuntualesSeleccionados = alumnosDetalle.filter(a => !a.esFijo);
                      const fechaActualTexto = getFechaDiaSeleccionado().format('DD/MM/YYYY');

                      return (
                        <div style={{ 
                          maxHeight: 'calc(80vh - 200px)',
                          overflowY: 'auto',
                          paddingRight: '8px'
                        }}>
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: 16,
                            marginBottom: 16,
                            paddingBottom: 8,
                            borderBottom: '1px solid #404040',
                            color: '#e0e0e0'
                          }}>
                            Configuración de Alumnos
                          </div>

                          {alumnosFijosSeleccionados.length > 0 && (
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                fontSize: 13,
                                marginBottom: 12,
                                color: '#b0b0b0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Alumnos Fijos
                              </div>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '12px'
                              }}>
                                {alumnosFijosSeleccionados.map(item => (
                                  <div
                                    key={item.id}
                                    className="alumno-card-compact"
                                    style={{
                                      border: '1px solid #404040',
                                      borderRadius: 8,
                                      padding: '12px',
                                      background: '#2d2d2d',
                                      transition: 'all 0.3s ease',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '8px',
                                      minHeight: '140px'
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#ffffff' }}>
                                      {item.alu_nombre} {item.alu_apellido}
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#b0b0b0' }}>
                                      Asiste todas las semanas
                                    </div>
                                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '11px', color: '#b0b0b0' }}>Asiste:</span>
                                        <Switch
                                          size="small"
                                          checked={item.asiste}
                                          checkedChildren="✓"
                                          unCheckedChildren="✗"
                                          onChange={(checked) => actualizarAsistenciaAlumno(item.id, checked)}
                                        />
                                      </div>
                                      <Space size="small" style={{ width: '100%' }}>
                                        <Button
                                          size="small"
                                          style={{ flex: 1, fontSize: '10px' }}
                                          onClick={() => marcarAlumnoComoFijo(item.id, false)}
                                        >
                                          Puntual
                                        </Button>
                                        <Button
                                          size="small"
                                          danger
                                          style={{ flex: 1, fontSize: '10px' }}
                                          onClick={() => quitarAlumnoDeSeleccion(item.id, { eliminarFijo: true })}
                                        >
                                          Quitar
                                        </Button>
                                      </Space>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {alumnosPuntualesSeleccionados.length > 0 && (
                            <div>
                              <div style={{ 
                                fontWeight: 600, 
                                fontSize: 13,
                                marginBottom: 12,
                                color: '#b0b0b0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Alumnos Puntuales
                              </div>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '12px'
                              }}>
                                {alumnosPuntualesSeleccionados.map(item => (
                                  <div
                                    key={item.id}
                                    className="alumno-card-compact"
                                    style={{
                                      border: '1px solid #404040',
                                      borderRadius: 8,
                                      padding: '12px',
                                      background: '#2d2d2d',
                                      transition: 'all 0.3s ease',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '8px',
                                      minHeight: '140px'
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#ffffff' }}>
                                      {item.alu_nombre} {item.alu_apellido}
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#b0b0b0' }}>
                                      Para el {fechaActualTexto}
                                    </div>
                                    <div style={{ marginTop: 'auto' }}>
                                      <Space size="small" style={{ width: '100%' }}>
                                        <Button
                                          size="small"
                                          danger
                                          style={{ flex: 1, fontSize: '10px' }}
                                          onClick={() => quitarAlumnoDeSeleccion(item.id)}
                                        >
                                          Quitar
                                        </Button>
                                        <Button
                                          size="small"
                                          style={{ flex: 1, fontSize: '10px' }}
                                          onClick={() => marcarAlumnoComoFijo(item.id, true)}
                                        >
                                          Fijo
                                        </Button>
                                      </Space>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {alumnosFijosSeleccionados.length === 0 && alumnosPuntualesSeleccionados.length === 0 && (
                            <div style={{ 
                              padding: '20px',
                              textAlign: 'center',
                              color: '#888',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              background: '#252525',
                              borderRadius: 8,
                              border: '1px dashed #404040'
                            }}>
                              No hay alumnos configurados
                            </div>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form>
          </Spin>
      </Modal>
    </MainLayout>
  );
};

export default AgendaCalendario;

